/**
 * The Preview of a command-line tool (utils/cliServices.js): it has no page, so instead of "Rendering … preview…" for
 * ever, "Try it": a command prefilled with the tool's name (`miactl --help`), Run, and what it printed (stdout,
 * stderr, exit code, time), through vfs-service's run-in-service (vfsClient.runInService: the runner's exec in the
 * service's preview dir, `go run` / `cargo run` / `node` / `python3` behind the tool's name). The last runs stay for
 * the browser session (sessionStorage), newest first; one click puts a command back. A viewer sees the runs but cannot
 * run (editors only, as the API). Not built since the sandbox woke: Build (a start, which only builds it). Not set up
 * as a command-line tool yet: Apps › How it runs says so. "Open terminal" opens the Terminal tab for anything more.
 */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input } from 'antd';
import { Play, TerminalWindow } from '@phosphor-icons/react';
import { runInService } from '../../utils/vfsClient';
import { startService } from '../../services/SandboxService';
import './CliTryIt.css';

/** Runs kept per service for the session. */
export const MAX_RUNS = 10;
const storageKey = (projectId, serviceName) => `flow:cli-runs:${projectId}:${serviceName}`;

function loadRuns(projectId, serviceName) {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(storageKey(projectId, serviceName)) || '[]');
    return Array.isArray(value) ? value.slice(0, MAX_RUNS) : [];
  } catch {
    return [];
  }
}

function saveRuns(projectId, serviceName, runs) {
  try {
    window.sessionStorage.setItem(storageKey(projectId, serviceName), JSON.stringify(runs.slice(0, MAX_RUNS)));
  } catch { /* a private window or a full storage: the runs live as long as the page */ }
}

const seconds = (ms) => (Number.isFinite(ms) ? `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s` : null);

/** One run: its command, exit code, time, and its output. */
function RunOutput({ run, onReuse = null }) {
  const failed = run.timedOut || (run.exitCode !== 0 && run.exitCode !== null);
  return (
    <div className="cli-run" data-testid="cli-run">
      <div className="cli-run-head">
        <code className="cli-run-command">{`$ ${run.command}`}</code>
        <span className={`cli-run-exit ${failed ? 'is-bad' : 'is-ok'}`} data-testid="cli-run-exit">
          {run.timedOut ? 'Timed out' : `exit ${run.exitCode ?? '?'}`}
          {seconds(run.durationMs) ? ` · ${seconds(run.durationMs)}` : ''}
        </span>
        {onReuse && <button className="cli-run-reuse" onClick={() => onReuse(run.command)} type="button">Use again</button>}
      </div>
      {run.stdout && <pre className="cli-run-out" data-testid="cli-run-stdout">{run.stdout}</pre>}
      {run.stderr && <pre className="cli-run-out is-err" data-testid="cli-run-stderr">{run.stderr}</pre>}
      {!run.stdout && !run.stderr && <p className="cli-run-none">It printed nothing.</p>}
    </div>
  );
}

RunOutput.propTypes = {
  onReuse: PropTypes.func,
  run: PropTypes.object.isRequired,
};

export default function CliTryIt({ projectId, serviceName, tool, canRun = true, configured = true, onOpenTerminal = null, onOpenHowItRuns = null }) {
  const [command, setCommand] = useState(`${tool} --help`);
  const [runs, setRuns] = useState(() => loadRuns(projectId, serviceName));
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [building, setBuilding] = useState(false);
  const short = serviceName.replace(/^services\//, '');

  const run = useCallback(async() => {
    const line = command.trim();
    if (!line || running) { return; }
    setRunning(true);
    setError(null);
    try {
      const answer = await runInService(projectId, { service: serviceName, command: line });
      const entry = {
        command: line,
        exitCode: answer?.exit_code ?? null,
        timedOut: Boolean(answer?.timed_out),
        durationMs: answer?.duration_ms ?? null,
        stdout: answer?.stdout || '',
        stderr: answer?.stderr || (answer?.timed_out ? answer?.error || '' : ''),
        at: Date.now(),
      };
      setRuns((before) => {
        const next = [entry, ...before].slice(0, MAX_RUNS);
        saveRuns(projectId, serviceName, next);
        return next;
      });
    } catch (err) {
      setError({ message: err?.message || 'The command could not run', code: err?.code || null });
    } finally {
      setRunning(false);
    }
  }, [command, projectId, running, serviceName]);

  const build = async() => {
    setBuilding(true);
    try {
      await startService(projectId, serviceName);
      setError({ message: `${short} is building: run the command again in a moment.`, code: 'building' });
    } catch (err) {
      setError({ message: err?.message || 'The build could not start', code: null });
    } finally {
      setBuilding(false);
    }
  };

  const [last, ...earlier] = runs;
  return (
    <div className="cli-try-it" data-testid="cli-try-it">
      <div className="cli-try-head">
        <h3>{`Try ${tool}`}</h3>
        <p className="cli-try-sub">
          {`${short} is a command-line tool: it has no page. Run it here, as you would in a terminal.`}
        </p>
      </div>
      {!configured && (
        <p className="cli-try-note" data-testid="cli-try-setup" role="status">
          {'Flow found a command-line tool. Set it up once in Apps › How it runs › Command-line tool, so it is built (and never started as a server).'}
          {onOpenHowItRuns && <> <button className="cli-run-reuse" onClick={onOpenHowItRuns} type="button">How it runs</button></>}
        </p>
      )}
      <div className="cli-try-form">
        <Input
          aria-label="Command"
          className="cli-try-input"
          data-testid="cli-try-command"
          disabled={!canRun || running}
          onChange={(event) => setCommand(event.target.value)}
          onPressEnter={run}
          prefix={<span aria-hidden className="cli-try-prompt">$</span>}
          value={command}
        />
        <Button data-testid="cli-try-run" disabled={!canRun || !command.trim()} icon={<Play aria-hidden size={14} />} loading={running} onClick={run} type="primary">Run</Button>
        {onOpenTerminal && (
          <Button data-testid="cli-try-terminal" icon={<TerminalWindow aria-hidden size={14} />} onClick={onOpenTerminal}>Open terminal</Button>
        )}
      </div>
      {!canRun && <p className="cli-try-note" data-testid="cli-try-viewer">Only the project&apos;s editors can run it.</p>}
      {error && (
        <div className="cli-try-error" data-testid="cli-try-error" role="alert">
          <span>{error.message}</span>
          {error.code === 'not_ready' && canRun && configured && (
            <Button data-testid="cli-try-build" loading={building} onClick={build} size="small">Build it</Button>
          )}
        </div>
      )}
      {last && <RunOutput run={last} />}
      {earlier.length > 0 && (
        <details className="cli-try-history" data-testid="cli-try-history">
          <summary>{`Earlier runs (${earlier.length})`}</summary>
          {earlier.map((item) => <RunOutput key={item.at} onReuse={canRun ? setCommand : null} run={item} />)}
        </details>
      )}
    </div>
  );
}

CliTryIt.propTypes = {
  // false for a viewer: the runs without the command and Run.
  canRun: PropTypes.bool,
  // false: Flow detected a command-line tool not set up as one yet (Apps › How it runs).
  configured: PropTypes.bool,
  onOpenHowItRuns: PropTypes.func,
  onOpenTerminal: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  // The tool's name, the command's first word (`miactl`).
  tool: PropTypes.string.isRequired,
};
